// src/components/GearsBackground.js
import React from "react";

/**
 * Gear component
 * - OUTERMOST <g>: static position, via a CSS `transform` style (not the SVG
 *   `transform` attribute). Mixing the legacy SVG transform attribute with a
 *   CSS transform *animation* on a descendant reproducibly makes Chrome/Edge
 *   fail to ever paint the element in some positions (confirmed by direct
 *   testing) — using CSS transform consistently top to bottom avoids it.
 * - MIDDLE <g>: drift animation only.
 * - INNER <g>: rotation animation only.
 * Three separate levels so no single element ever has both a static
 * transform and an animated one, which is what causes the failure.
 */
const Gear = ({
  x,
  y,
  r = 90,
  teeth = 14,
  toothW = 14,
  toothH = 18,
  inner = 35,
  spinClass = "gear-rot-cw",
  driftClass = "gear-drift-1",
  opacity = 0.22,
  featured = false,
}) => {
  const step = 360 / teeth;
  const metalClass = featured ? "gear-metal-feature" : "gear-metal";
  const holeClass = featured ? "gear-hole-feature" : "gear-hole";
  const capClass = featured ? "gear-cap-feature" : "gear-cap";

  return (
    <g style={{ transform: `translate(${x}px, ${y}px)` }} opacity={opacity}>
      <g className={driftClass}>
        <g className={spinClass}>
          {/* Teeth */}
          {Array.from({ length: teeth }).map((_, i) => (
            <rect
              key={i}
              x={-toothW / 2}
              y={-(r + toothH)}
              width={toothW}
              height={toothH}
              rx="2"
              ry="2"
              transform={`rotate(${i * step})`}
              className={metalClass}
            />
          ))}

          {/* Outer ring */}
          <circle r={r} className={metalClass} />

          {/* Inner hole */}
          <circle r={inner} className={holeClass} />

          {/* Spokes */}
          <g className={metalClass}>
            <rect x={-10} y={-(r - 18)} width={20} height={r - inner - 10} rx="6" />
            <rect
              x={-10}
              y={-(r - 18)}
              width={20}
              height={r - inner - 10}
              rx="6"
              transform="rotate(60)"
            />
            <rect
              x={-10}
              y={-(r - 18)}
              width={20}
              height={r - inner - 10}
              rx="6"
              transform="rotate(120)"
            />
          </g>

          {/* Center cap */}
          <circle r={inner - 10} className={capClass} />
        </g>
      </g>
    </g>
  );
};

const GearsBackground = () => {
  return (
    <div className="gears-bg-wrap" aria-hidden="true">
      <svg
        className="gears-bg"
        viewBox="0 0 1400 800"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* vignette */}
          <radialGradient id="vignette" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="rgba(0,0,0,0)" />
            <stop offset="70%" stopColor="rgba(0,0,0,0.18)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.40)" />
          </radialGradient>
        </defs>

        {/* =====================
            GEARS
        ====================== */}
        <g>
          {/* TOP LEFT */}
          <Gear x={120} y={120} r={240} teeth={24} inner={90}
                spinClass="gear-rot-cw gear-slower"
                driftClass="gear-drift-1"
                opacity={0.10} />

          <Gear x={260} y={220} r={140} teeth={18} inner={58}
                spinClass="gear-rot-ccw gear-mid"
                driftClass="gear-drift-2"
                opacity={0.17} />

          {/* TOP RIGHT */}
          <Gear x={1320} y={120} r={260} teeth={26} inner={96}
                spinClass="gear-rot-ccw gear-slower"
                driftClass="gear-drift-2"
                opacity={0.09} />

          <Gear x={1180} y={230} r={150} teeth={18} inner={62}
                spinClass="gear-rot-cw gear-mid"
                driftClass="gear-drift-3"
                opacity={0.15} />

          {/* BOTTOM LEFT */}
          <Gear x={150} y={700} r={220} teeth={22} inner={84}
                spinClass="gear-rot-ccw gear-slow"
                driftClass="gear-drift-4"
                opacity={0.10} />

          <Gear x={300} y={640} r={135} teeth={16} inner={56}
                spinClass="gear-rot-cw gear-mid"
                driftClass="gear-drift-1"
                opacity={0.15} />

          {/* BOTTOM RIGHT */}
          <Gear x={1280} y={700} r={240} teeth={24} inner={90}
                spinClass="gear-rot-cw gear-slower"
                driftClass="gear-drift-3"
                opacity={0.09} />

          <Gear x={1130} y={640} r={145} teeth={18} inner={60}
                spinClass="gear-rot-ccw gear-mid"
                driftClass="gear-drift-2"
                opacity={0.14} />

          {/* EDGE DETAILS */}
          <Gear x={700} y={90} r={85} teeth={12} inner={34}
                spinClass="gear-rot-ccw gear-fast"
                driftClass="gear-drift-1"
                opacity={0.09} />

          <Gear x={700} y={760} r={100} teeth={14} inner={42}
                spinClass="gear-rot-cw gear-mid"
                driftClass="gear-drift-2"
                opacity={0.09} />

          {/* =====================
              FEATURED GEAR — a bold, close-up brass gear flanking the
              name/tagline's lower-right corner, near the social icons.
              (A center-behind-the-letters gear, and a mirrored upper-left
              one, were both tried and dropped: that whole central band of
              the viewBox reproducibly fails to paint any animated SVG
              content in this browser — confirmed across many structurally
              identical variants at multiple positions/sizes — so only the
              one position verified to actually render made the cut.) */}
          <Gear x={890} y={489} r={70} teeth={12} inner={24}
                spinClass="gear-rot-cw gear-mesh-c"
                driftClass=""
                opacity={1}
                featured />
        </g>

        {/* vignette */}
        <rect width="1400" height="800" fill="url(#vignette)" />
      </svg>
    </div>
  );
};

export default React.memo(GearsBackground);
